import MatterCard from './matterCard';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { MatterStatus } from '../../constants/matter';
import { useFetchMatters } from '../../hooks/useFetchMatters';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useQueryClient } from '@tanstack/react-query';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import NoData from '../dashboard/noData';
import { BriefcaseEmpty } from '../icons';
import { IMatter } from '../../interface/matter';
import { useSearchParams } from 'react-router-dom';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import { IFilterOption } from '../../interface/transaction';
import { useMatterFilters } from '../../hooks/useMatterFilters';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import { useListViewPreference } from '../../context/ListViewPreferenceContext';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { MY_OWN_FILTER } from '../../constants/constants';
import { useOwnerFilter } from '../../hooks/useOwnerFilter';

const MattersListing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getActiveFavorite, setActiveFavorite } = useListViewPreference();
  const ownerFilter = useOwnerFilter({
    isMyOwnDefault: true,
    restoredValue: searchParams.get('matterOwner'),
  });
  const initialSearch = searchParams.get('matterSearch') || '';
  const initialFavorite = searchParams.get('matterFavorite')
    ? searchParams.get('matterFavorite') === 'true'
    : getActiveFavorite('matters');
  const initialInvoiceableType = searchParams.get('matterInvoiceableType') || '';
  const initialMatterTypeIds = useMemo(
    () => (searchParams.get('matterTypes') || '').split(',').filter(Boolean),
    [],
  );
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [isFavorite, setIsFavorite] = useState<boolean>(initialFavorite);
  const handleFavoriteToggle = (value: boolean) => {
    setIsFavorite(value);
    setActiveFavorite('matters', value);
  };
  const matterSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'openingDate',
        label: t('matter:openingDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'matterNumber',
        label: t('matter:matterNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
      {
        field: 'principal.name',
        label: t('matter:principal'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'openingDate',
    direction: 'DESC',
  });
  const {
    invoiceableTypeFilters,
    matterTypeFilters,
    selectedInvoiceableType,
    setSelectedInvoiceableType,
    selectedMatterTypes,
    setSelectedMatterTypes,
    selectedMatterFilters,
    matterFilterParams,
    removeMatterFilter,
    clearMatterFilters,
    hasMoreMatterTypes,
    isFetchingMoreMatterTypes,
    loadMoreMatterTypes,
  } = useMatterFilters({
    initialInvoiceableType,
    initialMatterTypeIds,
    matterTypeSearch: searchValue,
  });

  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: paginatedMatterData,
    isLoading: isMattersLoading,
  } = useFetchMatters({
    search: searchValue,
    pageSize: 10,
    extraFilters: {
      status: MatterStatus.ACTIVE,
      ...matterFilterParams,
    },
    isFavorite: isFavorite,
    myOwn: ownerFilter.isMyOwn,
    withFavoriteInformation: true,
    relations: [
      'responsibleUser:fullName',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
    ],
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    withDefaultTransactionType: true,
    withHasLinkedTransactionTypes: true,
    withHasSubMatters: true,
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'hasNotes',
      'currency',
      'language',
      'fixedPrice',
      'fixedPriceInUse',
      'parentMatterId',
      'insider',
      'language',
    ],
  });
  useEffect(() => {
    setSearchParams({
      matterSearch: searchValue,
      matterFavorite: isFavorite.toString(),
      matterInvoiceableType: selectedInvoiceableType?.value ?? '',
      matterTypes: selectedMatterTypes.map((item) => item.value).join(','),
      matterOwner: ownerFilter.value,
    });
  }, [searchValue, isFavorite, selectedInvoiceableType, selectedMatterTypes, ownerFilter.value]);

  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && paginatedMatterData?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };

  return (
    <CollapsibleListLayout
      testId="matters-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matter:search')}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['matters-list'] });
            return refetch();
          }}
          searchValue={searchValue}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          filterTitle={t('matter:filter.matterFilters')}
          ownerOptions={ownerFilter.options}
          ownerTitle={ownerFilter.title}
          setSelectedOwner={ownerFilter.select}
          radioTitle={t('matter:filter.invoiceableType')}
          checkboxTitle={t('matter:filter.matterType')}
          radioOptions={invoiceableTypeFilters}
          checkboxOptions={matterTypeFilters}
          selectedFilter={[...ownerFilter.selectedOptions, ...selectedMatterFilters]}
          setSelectedFilter={(val) => setSelectedInvoiceableType(val)}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => setSelectedMatterTypes(val)}
          clearFilter={(val?: string) => {
            if (!val) {
              ownerFilter.reset();
              clearMatterFilters();
              return;
            }
            if (val === MY_OWN_FILTER) {
              ownerFilter.reset();
              return;
            }
            removeMatterFilter(val);
          }}
          hasMoreCheckboxOptions={hasMoreMatterTypes}
          isFetchingMoreCheckboxOptions={isFetchingMoreMatterTypes}
          onLoadMoreCheckboxOptions={loadMoreMatterTypes}
          isFavouriteFilterActive={isFavorite}
          handleFavouriteToggle={handleFavoriteToggle}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={matterSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {Object.entries(paginatedMatterData).length > 0 &&
        paginatedMatterData?.map((val: IMatter) => {
          return <MatterCard key={val.id} matter={val} />;
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!paginatedMatterData ||
        (Object.entries(paginatedMatterData).length === 0 && !isFetching)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('matter:noMatters')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<BriefcaseEmpty />}
          />
        </div>
      )}
      {isMattersLoading ? (
        <SkeletonCardList rows={[1, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default MattersListing;
